import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFolder, faClipboard, faUser, faSearch, faSpinner } from '@fortawesome/free-solid-svg-icons';
import './GlobalSearchModal.css';
import PatientMetadataEditor from './ProjectDetails/PatientMetadataEditor/PatientMetadataEditor';

/**
 * Global search modal component that displays search results across projects, subprojects, and patients
 * 
 * @param {string} searchTerm - The search term to use
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Callback when the modal is closed
 * @param {function} onProjectSelect - Callback when a project is selected, receives projectId
 * @param {function} onSubprojectSelect - Callback when a subproject is selected, receives {parentProjectId, subprojectId}
 */
function GlobalSearchModal({ searchTerm, isOpen, onClose, onProjectSelect, onSubprojectSelect }) {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState({
    projects: [],
    subprojects: [],
    patients: []
  });
  const [patientToEdit, setPatientToEdit] = useState(null);
  const [currentEditingPatientId, setCurrentEditingPatientId] = useState(null);

  // Fetch results from real search endpoint when search term changes
  useEffect(() => {
    if (!isOpen || !searchTerm) return;
    
    const fetchResults = async () => {
      setLoading(true);
      
      try {
        // Call the real search endpoint
        const response = await fetch(`https://service9.szapfs.org/search?term=${encodeURIComponent(searchTerm)}`);
        
        if (!response.ok) {
          throw new Error(`Search request failed with status ${response.status}`);
        }
        
        const data = await response.json();
        
        // Log the response data
        console.log('Search endpoint response:', data);
        
        // Structure for our component - if the API returns a different structure,
        // we'll need to transform it here
        const formattedData = {
          projects: data.projects || [],
          subprojects: data.subprojects || [],
          patients: data.patients || []
        };
        
        setResults(formattedData);
      } catch (error) {
        console.error('Error fetching search results:', error);
      } finally {
        setLoading(false);
      }
    };
    
    // Add a small delay to avoid too many requests while typing
    const debounceTimer = setTimeout(() => {
      fetchResults();
    }, 300);
    
    return () => clearTimeout(debounceTimer);
  }, [searchTerm, isOpen]);
  
  // Click handlers for different result types
  const handleProjectClick = (project) => {
    console.log('Project clicked:', project);
    if (project && project.id) {
      // First close the modal
      onClose();
      
      // If the parent provides a callback for project selection, call it with the ID
      if (typeof onProjectSelect === 'function') {
        onProjectSelect(project.id);
      }
    } else {
      console.error('Project is missing ID:', project);
    }
  };
  
  const handleSubprojectClick = (subproject) => {
    console.log('Subproject clicked:', subproject);
    if (subproject && subproject.project_id && subproject.parentProjectId) {
      // First close the modal
      onClose();
      
      // If the parent provides a callback for subproject selection
      if (typeof onSubprojectSelect === 'function') {
        onSubprojectSelect({
          parentProjectId: subproject.parentProjectId,
          subprojectId: subproject.project_id
        });
      }
    } else {
      console.error('Subproject is missing ID or parent project ID:', subproject);
    }
  };
  
  const handlePatientClick = (patient) => {
    console.log('Patient clicked (GlobalSearchModal):', patient);
    if (patient && patient.patientdata && patient.patient_id) {
      setPatientToEdit(patient.patientdata);
      setCurrentEditingPatientId(patient.patient_id); // Store the original patient_id
    } else {
      console.error('Patient data or patient_id is missing. Patient object:', patient);
      alert('Cannot edit patient: essential information missing.');
      onClose(); 
    }
  };

  const handleEditorClose = () => {
    setPatientToEdit(null);
    setCurrentEditingPatientId(null); // Clear the stored patient_id
    onClose(); // Call the original onClose prop to close the modal/view
  };

  const handleEditorSave = async (updatedData) => {
    console.log('PatientMetadataEditor attempting to save data (GlobalSearchModal):', updatedData);
    console.log('Using patient_id for save (GlobalSearchModal):', currentEditingPatientId);

    if (!currentEditingPatientId) {
      console.error("Original patient_id is missing. Cannot save.", currentEditingPatientId);
      alert("Error: Critical patient identifier is missing. Cannot save.");
      return;
    }

    try {
      const bodyPayload = {
        patient_id: currentEditingPatientId, // Use the stored original patient_id
        ...updatedData
      };

      console.log('Sending payload to /patient/metadata (GlobalSearchModal):', bodyPayload);

      const response = await fetch('https://service9.szapfs.org/patient/metadata', {
        method: 'PUT', 
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bodyPayload)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Failed to update patient metadata: ${response.status} ${errorText}`, bodyPayload);
        throw new Error(`Failed to update patient metadata: ${response.status} ${errorText}`);
      }

      // Assuming the API returns some confirmation or the updated object, though ProjectDetails doesn't use the direct response body.
      console.log("Patient metadata updated successfully via GlobalSearchModal.");

      // Successfully saved, now close editor and modal
      setPatientToEdit(null);
      setCurrentEditingPatientId(null); // Clear the stored patient_id
      onClose(); 
    } catch (error) {
      console.error("Error updating patient metadata via GlobalSearchModal:", error);
      alert(`Error saving patient information: ${error.message}. Please check console for details.`);
    }
  };
  
  if (!isOpen) return null;
  
  if (patientToEdit) {
    return (
      <PatientMetadataEditor
        patient={patientToEdit}
        onClose={handleEditorClose}
        onSave={handleEditorSave}
      />
    );
  }

  const hasResults = results.projects.length > 0 || 
                     results.subprojects.length > 0 || 
                     results.patients.length > 0;
  
  return (
    <div className="GSM-container">
      {loading ? (
        <div className="GSM-loading">
          <FontAwesomeIcon icon={faSpinner} className="GSM-loading-icon" spin />
          <div className="GSM-loading-text">Searching...</div>
        </div>
      ) : (
        <>
          {!hasResults && searchTerm && (
            <div className="GSM-no-results">No results found for "{searchTerm}"</div>
          )}
          
          {hasResults && (
            <div className="GSM-results">
              {results.projects.length > 0 && (
                <div className="GSM-section">
                  <div className="GSM-section-title">
                    <FontAwesomeIcon icon={faFolder} className="GSM-section-icon" />
                    <span>Projects</span>
                  </div>
                  {results.projects.map(project => (
                    <div 
                      key={project.id} 
                      className="GSM-result-item GSM-project-item"
                      onClick={() => handleProjectClick(project)}
                    >
                      <div className="GSM-result-title">
                        <FontAwesomeIcon icon={faFolder} className="GSM-item-icon" />
                        {project.name}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {results.subprojects.length > 0 && (
                <div className="GSM-section">
                  <div className="GSM-section-title">
                    <FontAwesomeIcon icon={faClipboard} className="GSM-section-icon" />
                    <span>Subprojects</span>
                  </div>
                  {results.subprojects.map(subproject => (
                    <div 
                      key={subproject.id} 
                      className="GSM-result-item GSM-subproject-item"
                      onClick={() => handleSubprojectClick(subproject)}
                    >
                      <div className="GSM-result-title">
                        <FontAwesomeIcon icon={faClipboard} className="GSM-item-icon" />
                        {subproject.project_id}
                        <span className="GSM-field meta" style={{ marginLeft: 'auto' }}><strong>{subproject.parentProjectName} (#{subproject.parentProjectId})</strong></span>
                      </div>
                      <div className="GSM-result-description">{subproject.description}</div>
                    </div>
                  ))}
                </div>
              )}
              
              {results.patients.length > 0 && (
                <div className="GSM-section">
                  <div className="GSM-section-title">
                    <FontAwesomeIcon icon={faUser} className="GSM-section-icon" />
                    <span>Patients</span>
                  </div>
                  {results.patients.map((patient, index) => (
                    <div 
                      key={patient.patient_id || index} 
                      className="GSM-result-item GSM-patient-item"
                      onClick={() => handlePatientClick(patient)}
                    >
                      <div className="GSM-result-title">
                        <FontAwesomeIcon icon={faUser} className="GSM-item-icon" />

                        {patient.name}
                        <span className="GSM-field" style={{ marginLeft: 'auto' }}><strong>{patient.patient_id}</strong></span>

                      </div>
                      <div className="GSM-result-description">
                        <div className="GSM-info-group project-info">
                          <div className="GSM-info-label">Project</div>
                          <div className="GSM-info-content">
                            <span className="GSM-field"><strong>{patient.parentProjectName}</strong></span>
                            {patient.parentProjectId && <span className="GSM-field meta"><strong>ID : {patient.parentProjectId}</strong></span>}
                          </div>
                        </div>
                        
                        <div className="GSM-info-group subproject-info">
                          <div className="GSM-info-label">Subproject</div>
                          <div className="GSM-info-content">
                            {patient.subprojectDescription && <span className="GSM-field"><strong>{patient.subprojectDescription}</strong></span>}
                            <span className="GSM-field meta"><strong>ID : {patient.subprojectId}</strong></span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default GlobalSearchModal;
